import axios from 'axios';
import appStore from '@/utils/store';
import { isTruthyEnv } from '@/services/referrals';

const BASE_API_URL =
    import.meta.env['VITE_MEEFOD_API_URL'] ||
    'https://us-central1-meyo-230113.cloudfunctions.net/apiCaloo';

// ── Types ──

export type SsoExchangeWebResponse = {
    webToken: string;
    prefill: {
        name?: string;
        email?: string;
        locale?: string;
        sportProfile?: string;
    };
    branding: {
        logoUrl: string;
        primaryColor: string;
        partnerName: string;
        tagline?: string;
    };
    partnerClaims: {
        partnerId: string;
        partnerUserId?: string;
    };
};

export type SsoSession = {
    partnerId: string;
    webToken: string;
    prefill: SsoExchangeWebResponse['prefill'];
    branding: SsoExchangeWebResponse['branding'];
    partnerClaims: SsoExchangeWebResponse['partnerClaims'];
    answers?: Record<string, any>;
    result?: any;
};

export type SsoProfilePayload = {
    status: 'onboarding_completed' | 'profile_saved';
    onboardingAnswers?: Record<string, any>;
    nijiInitialInsight?: any;
    consent?: {
        gdpr: boolean;
        marketing: boolean;
        at: number;
    };
};

export type SsoHandoffResponse = {
    code: string;
    deepLink: string;
    appScheme?: string;
};

// ── Fake mode helpers ──

function hashStr(input: string): number {
    let h = 2166136261;
    for (let i = 0; i < input.length; i++) {
        h ^= input.charCodeAt(i);
        h = Math.imul(h, 16777619);
    }
    return h >>> 0;
}

function makeRng(seed: number) {
    let x = seed >>> 0;
    return {
        next(): number {
            x ^= x << 13;
            x ^= x >>> 17;
            x ^= x << 5;
            return (x >>> 0) / 0xffffffff;
        },
    };
}

export function buildFakeSsoResponse(partnerId: string): SsoExchangeWebResponse {
    const seed = hashStr(partnerId);
    const rng = makeRng(seed);

    const colors = ['#4CE39D', '#6C63FF', '#FF6584', '#43BCCD', '#F9A826'];
    const colorIdx = Math.floor(rng.next() * colors.length);
    const primaryColor = colors[colorIdx] ?? '#4CE39D';

    const names: Record<string, string> = {
        wodbuster: 'WodBuster',
        fitlab: 'FitLab',
        nutriclub: 'NutriClub',
    };
    const partnerName = names[partnerId.toLowerCase()] ||
        partnerId.charAt(0).toUpperCase() + partnerId.slice(1);

    return {
        webToken: `fake_webtoken_${partnerId}_${seed.toString(16)}`,
        prefill: {
            name: 'Atleta Demo',
            email: 'demo@caloo.app',
            locale: 'es',
            sportProfile: 'strength',
        },
        branding: {
            logoUrl: '',
            primaryColor,
            partnerName,
            tagline: 'Tu rendimiento, optimizado',
        },
        partnerClaims: {
            partnerId,
            partnerUserId: `fake_user_${seed.toString(16)}`,
        },
    };
}

function buildFakeHandoffResponse(partnerId: string): SsoHandoffResponse {
    const seed = hashStr(partnerId + '_handoff');
    return {
        code: `fake_code_${seed.toString(16)}`,
        deepLink: `https://link.caloo.app/sso?code=fake_code_${seed.toString(16)}&partner=${partnerId}`,
        appScheme: `caloo://sso?code=fake_code_${seed.toString(16)}`,
    };
}

// ── API calls ──

export async function exchangeSsoCode(code: string): Promise<SsoExchangeWebResponse> {
    const res = await axios.post(
        `${BASE_API_URL}/sso/partner/exchange-web`,
        { code },
        {
            headers: {
                'Content-Type': 'application/json',
                Authorization: import.meta.env['VITE_MEFOOD_API_TOKEN'],
            },
        },
    );
    return res.data as SsoExchangeWebResponse;
}

export async function savePartnerProfile(webToken: string, payload: SsoProfilePayload): Promise<void> {
    await axios.post(
        `${BASE_API_URL}/sso/partner/profile`,
        payload,
        {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${webToken}`,
            },
        },
    );
}

export async function getHandoffCode(webToken: string): Promise<SsoHandoffResponse> {
    const res = await axios.post(
        `${BASE_API_URL}/sso/partner/handoff`,
        {},
        {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${webToken}`,
            },
        },
    );
    return res.data as SsoHandoffResponse;
}

// ── Branding ──

export function applyPartnerBranding(branding: SsoExchangeWebResponse['branding']): void {
    const root = document.documentElement;
    if (branding.primaryColor) {
        root.style.setProperty('--partner-primary', branding.primaryColor);
    }
    if (branding.logoUrl) {
        root.style.setProperty('--partner-logo', `url('${branding.logoUrl}')`);
    }
}

// ── Session helpers (appStore-backed) ──

export function getSsoSession(): SsoSession | null {
    return appStore.get('ssoSession') as SsoSession | null;
}

export function setSsoSession(session: SsoSession): void {
    appStore.set('ssoSession', session);
}

export function clearSsoSession(): void {
    appStore.delete('ssoSession');
}

// ── Entry point used by start page ──

export async function initSsoSession(
    partnerId: string,
    code: string | null,
): Promise<SsoSession | null> {
    if (isTruthyEnv(import.meta.env['VITE_FAKE_SSO'])) {
        await new Promise((r) => setTimeout(r, 600));
        const fake = buildFakeSsoResponse(partnerId);
        const session: SsoSession = { partnerId, ...fake };
        setSsoSession(session);
        applyPartnerBranding(fake.branding);
        return session;
    }

    if (!code) return null;

    try {
        const data = await exchangeSsoCode(code);
        const session: SsoSession = { partnerId, ...data };
        setSsoSession(session);
        applyPartnerBranding(data.branding);
        return session;
    } catch {
        return null;
    }
}

// ── Handoff (wraps fake mode) ──

export async function performHandoff(webToken: string, partnerId: string): Promise<SsoHandoffResponse> {
    if (isTruthyEnv(import.meta.env['VITE_FAKE_SSO'])) {
        await new Promise((r) => setTimeout(r, 400));
        return buildFakeHandoffResponse(partnerId);
    }
    return getHandoffCode(webToken);
}
